import { FunctionComponent, useRef, useState } from 'react'
import {
  Box,
  IconButton,
  Link,
  rucksack,
  Typography,
} from '@black-bear-energy/rucksack'
import { DeleteRounded, UploadFile } from '@mui/icons-material'
import { exceedsMaxUploadSize, maxDocumentUploadSizeMB } from '../../utils/file'

export const FileUploadInput: FunctionComponent<FileUploadInputProps> = ({
  onSelectedFileChange,
  singleFileSelect = false,
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [hasSizeError, setHasSizeError] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSelectedFileChange = (selectedFiles: File[]) => {
    if (exceedsMaxUploadSize(selectedFiles)) {
      setHasSizeError(true)
      return
    }
    setHasSizeError(false)
    onSelectedFileChange(selectedFiles)
    setSelectedFiles(selectedFiles)
  }

  /** When the user clicks the 'Browse' link, open the file input. */
  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  /**
   * Handle file(s) that the user selects in the file explorer.
   * @param event - The change event.
   */
  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || !files.length) {
      return
    }
    const fileArray = getFileArray(files)
    handleSelectedFileChange(fileArray)
  }

  /** Convert a FileList to File[] */
  const getFileArray = (fileList: FileList): File[] => {
    const fileArray: File[] = []
    for (let i = 0; i < fileList.length; i++) {
      if (singleFileSelect && i > 0) {
        break
      }
      fileArray.push(fileList[i])
    }
    return fileArray
  }

  const handleDrag = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (event.type === 'dragenter' || event.type === 'dragover') {
      setIsDragging(true)
    } else if (event.type === 'dragleave') {
      setIsDragging(false)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    const files = getFileArray(event.dataTransfer.files)
    handleSelectedFileChange(files)
  }

  const fileInputId = `uploadDocumentModalInput`
  const filesSelected = selectedFiles.length > 0

  return (
    <>
      {filesSelected ? (
        <Box
          sx={{
            display: 'flex',
            padding: '16px',
            alignItems: 'center',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
          <UploadFile sx={{ color: rucksack.palette.primary.main }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              flex: '1 0 0',
            }}
          >
            {selectedFiles.map((file, index) => (
              <Typography variant="subtitle1" key={index}>
                {file.name}
              </Typography>
            ))}
          </Box>
          <IconButton
            onClick={() => handleSelectedFileChange([])}
            sx={{ height: '40px', width: '40px', padding: '6px' }}
          >
            <DeleteRounded />
          </IconButton>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            padding: '24px 16px',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            alignSelf: 'stretch',
            border: '1px dashed',
            borderColor: hasSizeError
              ? rucksack.palette.error.main
              : isDragging
              ? rucksack.palette.primary.main
              : rucksack.palette.divider,
            borderRadius: rucksack.shape.borderRadius,
            backgroundColor: hasSizeError
              ? rucksack.palette.error.states.selected
              : isDragging
              ? 'rgba(73, 173, 214, 0.24)'
              : 'none',
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <UploadFile
            sx={{
              color: hasSizeError
                ? rucksack.palette.error.main
                : rucksack.palette.primary.main,
            }}
          />
          <input
            ref={fileInputRef}
            id={fileInputId}
            data-testid={fileInputId}
            type="file"
            multiple={!singleFileSelect}
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Link
              href="#"
              onClick={handleBrowseClick}
              sx={{
                // Override the pink hover and focus color defined in index.css.
                '&:hover, &:focus': {
                  color: rucksack.palette.primary.main,
                },
              }}
            >
              <Typography variant="body1">Browse</Typography>
            </Link>
            <Typography variant="subtitle1">or drag and drop</Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: rucksack.palette.text.secondary }}
          >
            PDF, CSV/XLS, DOCX, ZIP, or image
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: hasSizeError
                ? rucksack.palette.error.main
                : rucksack.palette.text.secondary,
            }}
          >
            {hasSizeError
              ? `File exceeds the maximum allowed size of ${maxDocumentUploadSizeMB} MB.`
              : `Max file size: ${maxDocumentUploadSizeMB} MB`}
          </Typography>
        </Box>
      )}
    </>
  )
}

interface FileUploadInputProps {
  onSelectedFileChange: (files: File[]) => void
  singleFileSelect?: boolean
}
